import { z } from 'zod';
import { idSchema, unitKeySchema } from './common.js';
import { seedEquipmentSchema } from './seed.js';
/**
 * Recipe import (D15): URL / pasted-text → reviewable draft → recipe.
 * The draft mirrors the seed shapes so the review UI and the recipe editor
 * can share components.
 */
export const importConfidenceSchema = z.enum(['high', 'medium', 'low']);
export const importDraftIngredientSchema = z.object({
    ingredient: z.string().max(120),
    qty: z.number().positive().nullable(),
    qtyMax: z.number().positive().nullable().default(null),
    unit: unitKeySchema,
    prep: z.string().max(200).nullable().default(null),
    optional: z.boolean().default(false),
    raw: z.string().max(400),
    confidence: importConfidenceSchema.default('medium'),
    notes: z.array(z.string().max(200)).default([]),
});
export const importDraftStepSchema = z.object({
    text: z.string().min(1).max(2000),
    durationMin: z.number().positive().nullable().default(null),
    attended: z.boolean().default(true),
    timer: z.boolean().default(false),
    canDoAhead: z.boolean().default(false),
    equipment: seedEquipmentSchema.optional(),
});
export const importDraftSourceSchema = z.object({
    kind: z.enum(['url_import', 'other']),
    ref: z.string().max(500),
    siteName: z.string().max(100).nullable().default(null),
    author: z.string().max(100).nullable().default(null),
    licence: z.string().max(200),
});
export const importDraftSchema = z.object({
    title: z.string().max(200).nullable(),
    description: z.string().max(2000).nullable().default(null),
    servings: z.number().int().positive().nullable().default(null),
    prepMin: z.number().nonnegative().nullable().default(null),
    cookMin: z.number().nonnegative().nullable().default(null),
    totalMin: z.number().nonnegative().nullable().default(null),
    /** The source page's image URL. On approval the API downloads it (SSRF-guarded)
     *  and re-processes it as the household's recipe photo (PLAN §4.6). */
    imageUrl: z.string().max(1000).nullable().default(null),
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
    ingredients: z.array(importDraftIngredientSchema).max(200).default([]),
    steps: z.array(importDraftStepSchema).max(200).default([]),
    source: importDraftSourceSchema,
    reviewNotes: z.array(z.string().max(300)).default([]),
});
export const IMPORT_STATUSES = ['queued', 'fetching', 'parsed', 'failed', 'done'];
export const importJobStatusSchema = z.enum(IMPORT_STATUSES);
export const importReviewStateSchema = z.enum(['pending', 'approved', 'rejected']);
export const importJobDtoSchema = z.object({
    id: idSchema,
    status: importJobStatusSchema,
    reviewState: importReviewStateSchema,
    sourceUrl: z.string().nullable(),
    error: z.string().nullable(),
    draft: importDraftSchema.nullable(),
    createdRecipeId: idSchema.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** POST /api/v1/imports/url */
export const importUrlRequestSchema = z.object({
    url: z.url().max(500),
});
/** POST /api/v1/imports/text */
export const importTextRequestSchema = z.object({
    title: z.string().trim().max(200).optional(),
    text: z.string().min(10).max(20_000),
});
/** PATCH /api/v1/imports/:id */
export const updateImportRequestSchema = z.object({
    draft: importDraftSchema,
});
export const importJobResponseSchema = z.object({ job: importJobDtoSchema });
export const importJobsResponseSchema = z.object({ jobs: z.array(importJobDtoSchema) });
export const approveImportResponseSchema = z.object({
    job: importJobDtoSchema,
    recipeId: idSchema,
});
//# sourceMappingURL=imports.js.map